import { capitalize } from '../../utils/helpers';

export const REVIEW_ELEMENTS = ['presentation', 'novelty', 'validation', 'reproducibility', 'significance'];

export const REVIEW_SCORE_EXPLAIN = {
  novelty: [
    '1 - All significant contributions exist in prior work',
    '2 - Minor changes are made over prior work',
    '3 - The contributions are marginally novel compared to prior work',
    '4 - The contributions are significant, but aspects appear in prior work',
    '5 - The contributions are significant, and do not appear in prior work',
  ],
  validation: [
    '1 - No meaningful evaluations are performed',
    '2 - Significant evaluations are missing and comparisons to prior work are missing',
    '3 - Additional validations are required including comparisons and ablation studies',
    '4 - The validation is thorough, but some comparisons or ablation studies are missing',
    '5 - The validation is thorough, including all relevant comparisons and ablation studies',
  ],
  significance: [
    '1 - The scope of the paper is very limited and the potential impact is marginal',
    '2 - The proposed idea is interesting, but may be difficult to extend or build on ',
    '3 - The paper contains a few key insights that may be useful ',
    '4 - The key contributions are interesting and may influence future work',
    '5 - A significant paper with a high potential impact on the community',
  ],
  presentation: [
    '1 - The paper is unclear in its presentation and difficult to follow',
    '2 - The writing is clear, but the overall contributions are not well-presented or clear',
    '3 - The paper is well-written, but the method presentation could be improved ',
    '4 - The paper is well-written and easy to follow, but some minor revisions may strengthen the paper',
    '5 - The paper is extremely well-written and easy to follow',
  ],
  reproducibility: [
    '1 - It would be very difficult to reproduce the proposed method, analysis, and results',
    '2 - Some major details are missing, but a good approximation should be possible to reproduce',
    '3 - Most details are provided, but a few detail are missing',
    '4 - All important details for reproducing the paper are provided, but code is missing',
    '5 - All important details and code are provided by the authors',
  ],
};

export const REVIEW_PROMPTS = {
  novelty:
    'Assess the paper’s originality and novelty. Remember that simple ideas can still be novel. Remember to be open to new ideas.',
  validation:
    " Assess the quality of validation and evaluation. Is the paper well-validated? Does the validation support the claims of the paper? Does the paper compare against relevant methods? Are there any missing baselines? Remember that it's okay for papers not to improve over all existing methods. Are the contributions of the method adequately validated via ablation studies?",
  significance:
    'Assess the potential impact this paper will have on the community. Could the ideas introduced here be useful and easily adapted to new problems/applications?',
  presentation:
    'Assess the paper’s presentation and quality of writing. Are the ideas and main contributions clear? Is the writing easy to follow? Are relevant papers accurately reviewed? Does the paper clearly discuss the limitations and drawbacks?',
  reproducibility:
    'Are all important details (e.g., algorithm, training procedure, evaluation metric(s)) adequately discussed? Could the proposed method be reproduced from the paper (and code, if provided)? Are all evaluations and comparisons reproducible?',
  paperBrief: 'Briefly summarize the paper including its main contributions.',
  updateSummary:
    "Explain the reasoning behind your final rating and discuss the main strengths and weaknesses of the paper. If appropriate, include suggestions for improving the submission. If your rating changed during the reviewer discussion phase, shortly explain why you've changed your ratings.",
};

export const HOVER_ELEMENT_PROMPTS = {
  novelty: 'Paper’s originality and novelty',
  validation: 'Quality of validation and evaluation',
  significance: 'Potential impact of this paper',
  presentation: 'Presentation of ideas and quality of writing',
  reproducibility: 'Can the results be easily reproduced',
};

export const BADGE_TYPES = {
  FIRST_REVIEW: { label: 'First Review', desc: 'Completed a first review 😄' },
  MONTH_STRIKE: { label: 'Month Bingo', desc: 'Filled a review every week for a month 📅' },
  SAMESIES: { label: 'Samesies', desc: 'Filled the same scores as another reviewer 😮' },
  EARLY_BIRD: { label: 'Early Bird', desc: 'Finished a review in the first three days 😎' },
  EMERGENCY_REVIEWER: { label: 'Emergency Reviewer', desc: 'Served as an emergency reviewer 🦺' },
};

export const CHIPS_TYPES = {
  SINGLE_BLIND: { label: 'Single-Blind', desc: 'The authors of this paper are not anonymous' },
  DOUBLE_BLIND: {
    label: 'Double-Blind',
    desc: 'Both the authors and the reviewers of this paper are anonymous',
  },
  MATCH_SCORE: { label: 'Match Score', desc: 'How well this paper matches you reviewing preferences' },
  AVERAGE_SCORE: { label: 'Average Score', desc: 'Average review score' },
  REVIEWER_REWARD: { label: 'Reviewer Reward', desc: 'Reputation points rewarded for reviewing this paper' },
};

export const TWITTER_LINK = 'https://twitter.com/peerxiv';

export const MOCK_END = "Oops! The mock doesn't go that way, keep exploring 🗺️";

export const REGISTER_PAPER = 'Registered successfully! you will be notified if the paper is assigned to you';

export const PRIMARY_TOPICS = ['Computer Vision', 'Natural Language Processing', 'Reinforcement Learning', 'Robotics'];

export const SUBJECT_OPTIONS = {
  DET: 'Object Detection and Tracking',
  ACTION_RECOGNITION: 'Action Recognition and Video Analysis',
  GANS: 'GANs and Image Editing',
  SUPER_RESOLUTION: 'Image Super Resolution',
  IMAGE2IMAGE: 'Image-to-Image Translation',
  DIFFUSION: 'Diffusion Models',
  DOCUMENT: 'OCR and Document Analysis',
  FEW_SHOT: 'Few-Shot Learning',
  SEGMENT: 'Semantic Segmentation',
  DDD: '3D Reconstruction',
  ARCHITECTURES: 'Network Architectures',
  VIDEO: 'Video Analysis and Understanding',
  ADAPT: 'Domain Adaptation and Transfer Learning',
  POSE: 'Pose Estimation and Keypoint Detection',
};

export const SUBJECT_OPTIONS_NLP = {
  MODELING: 'Language Modeling',
  QUESTION: 'Question Answering',
  TRANSLATE: 'Machine Translation',
  SENTI: 'Sentiment Analysis',
  NER: 'Named Entity Recognition',
  SUMM: 'Text Summarization',
  EXTRACT: 'Information Extraction',
  CLASS: 'Text Classification',
};

export const DATE_OPTIONS = {
  WEEK: 'This Week',
  MONTH: 'This Month',
  YEAR: 'This Year',
  ALLTIME: 'All Time',
};

export const DAY = 1000 * 60 * 60 * 24;
export const MILISECONDS_IN_DATE = {
  [DATE_OPTIONS.WEEK]: 7 * DAY,
  [DATE_OPTIONS.MONTH]: 30 * DAY,
  [DATE_OPTIONS.YEAR]: 365 * DAY,
};

export const OTHER_OPTIONS = {
  DOUBLE: 'Double-Blind',
  CODE: 'Released Code',
};

const REVIEW_ELEMENTS_OBJECT = Object.fromEntries(REVIEW_ELEMENTS.map((type) => [type, `${capitalize(type)}`]));
export const REVIEWED_SORT_OPTIONS = {
  OLDEST: 'Oldest',
  NEWEST: 'Newest',
  RANK: 'Overall Rank',
  ...REVIEW_ELEMENTS_OBJECT,
};

export const REVIEWER_PAST_SORT_OPTIONS = {
  OLDEST: 'Oldest',
  NEWEST: 'Newest',
  RANK: 'Overall Rank',
  BOUNTY: 'Bounty Received',
  ...REVIEW_ELEMENTS_OBJECT,
};

export const SUBMITTED_SORT_OPTIONS = {
  OLDEST: 'Oldest',
  NEWEST: 'Newest',
  REWARD: 'Reward',
  MATCH: 'Match',
};

export const POSITIONS = [
  'Undergraduate Student',
  'MSc Student',
  'Phd Student',
  'Postdoctoral Researcher',
  'Lecturer',
  'Assistant Professor',
  'Associate Professor',
  'Full Professor',
  'Emeritus',
  'Researcher',
  'Principal Researcher',
];
export const REVIEW_STATES = {
  PRE_REVIEW: { name: 'pre_review' },
  FIRST_ROUND: {
    name: 'first_round',
    time_limit: 14,
    display: true,
    title: 'Individual Reviews',
    titleShort: 'Reviews',
  },
  DISCUSSION: {
    name: 'discussion',
    time_limit: 7,
    display: true,
    title: 'Reviewers Discussion',
    titleShort: 'Discussion',
  },
  AUTHOR_RESPONSE: {
    name: 'author_response',
    time_limit: 7,
    display: true,
    title: 'Author Response',
    titleShort: 'Response',
  },
  POST_REVIEW: { name: 'post_review' },
};

export const PAPER_VIEW_MODE = {
  generalReviewedPapers: 'general_reviewed_papers',
  reviewerExplorePapers: 'reviewer_explore_papers',
  reviewerActivePapers: 'reviewer_active_papers',
  reviewerPastReviews: 'reviewer_past_reviews',
  authorSubmitted: 'author_submitted',
  authorActiveReview: 'author_active_review',
};
